import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { AgentCurrentTicketsFilters } from "./AgentCurrentTicketsFilters";
import { fetchAgentTicketsSummary, fetchAgentTicketsReport } from "./reportsApi";
import { TicketReport } from "./TicketReport";
import {
  convertDateStringToDate,
  convertDateToDateString,
  setStartOfDayToDateString,
  setEndOfDayToDateString,
} from "../../common/utils/dateHelper";
import { routes } from "../../common/routing/routes";
import {
  defaultPageSize,
  defaultPaginationOptions,
  emptyPaginationDetails,
  getPaginationFirst,
  getPaginationOptionsFromDataTable,
} from "../../common/api/paginationHelper";
import FormGroup from "../../common/components/form/FormGroup";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import ReportsFiltersRelativeDates, { relativeDates } from "./ReportsFiltersRelativeDates";
import { AgentTicketsReportSummary } from "./AgentTicketsReportSummary";

export default function AgentCurrentTicketsReport() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AgentTicketsReportSummary>();
  const [ticketsReport, setTicketsReport] = useState<TicketReport[]>();
  const [filters, setFilters] = useState<AgentCurrentTicketsFilters>();
  const [pagination, setPagination] = useState(emptyPaginationDetails);
  const [first, setFirst] = useState(() => getPaginationFirst(pagination.pageNumber, defaultPageSize));
  const [rows, setRows] = useState(defaultPageSize);
  const [relativeDateId, setRelativeDateId] = useState(relativeDates.currentMonth.id);
  const { control, handleSubmit, setValue } = useForm<AgentCurrentTicketsFilters>({
    defaultValues: {
      closedStartDate: relativeDates.currentMonth.startDate,
      closedEndDate: relativeDates.currentMonth.endDate,
    },
  });

  const onSubmit = async (data: AgentCurrentTicketsFilters) => {
    const appliedFilters = {
      closedStartDate: setStartOfDayToDateString(data.closedStartDate)!,
      closedEndDate: setEndOfDayToDateString(data.closedEndDate)!,
    };
    setFilters(appliedFilters);
    setFirst(0);

    const [summaryResult, ticketsResult] = await Promise.all([
      fetchAgentTicketsSummary(appliedFilters),
      fetchAgentTicketsReport(appliedFilters, getPaginationOptionsFromDataTable(0, rows)),
    ]);
    setSummary(summaryResult);
    setPagination(ticketsResult.pagination);
    setTicketsReport(ticketsResult.ticketsReport);
  };

  const onPageChange = async (newFirst: number, newRows: number) => {
    if (!filters) {
      return;
    }

    setFirst(newFirst);
    setRows(newRows);
    const result = await fetchAgentTicketsReport(filters, getPaginationOptionsFromDataTable(newFirst, newRows));
    setPagination(result.pagination);
    setTicketsReport(result.ticketsReport);
  };

  return (
    <div className="flex flex-column gap-2">
      <Card title="Raport agent">
        <div className="flex flex-column gap-4">
          <ReportsFiltersRelativeDates
            value={relativeDateId}
            onChange={(id) => {
              setRelativeDateId(id);
              setValue("closedStartDate", relativeDates[id].startDate);
              setValue("closedEndDate", relativeDates[id].endDate);
            }}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid align-items-end">
              <div className="col-12 sm:col-4">
                <FormGroup size="small" label="Data inceput inchidere">
                  <Controller
                    name="closedStartDate"
                    control={control}
                    render={({ field }) => (
                      <Calendar
                        showIcon
                        dateFormat="dd/mm/yy"
                        value={convertDateStringToDate(field.value)}
                        onChange={(e) => field.onChange(convertDateToDateString(e.target.value ?? undefined))}
                      />
                    )}
                  />
                </FormGroup>
              </div>
              <div className="col-12 sm:col-4">
                <FormGroup size="small" label="Data sfarsit inchidere">
                  <Controller
                    name="closedEndDate"
                    control={control}
                    render={({ field }) => (
                      <Calendar
                        showIcon
                        dateFormat="dd/mm/yy"
                        value={convertDateStringToDate(field.value)}
                        onChange={(e) => field.onChange(convertDateToDateString(e.target.value ?? undefined))}
                      />
                    )}
                  />
                </FormGroup>
              </div>
              <div className="col-12 sm:col-4">
                <Button type="submit" className="mb-1" label="Genereaza" icon="pi pi-search" />
              </div>
            </div>
          </form>
        </div>
      </Card>

      {summary && (
        <div className="grid">
          <div className="col-12 sm:col-6">
            <Card title="In lucru">
              <div className="text-4xl font-bold">{summary.inWorkCount}</div>
            </Card>
          </div>
          <div className="col-12 sm:col-6">
            <Card title="Inchise">
              <div className="text-4xl font-bold">{summary.closedCount}</div>
            </Card>
          </div>
        </div>
      )}

      {ticketsReport && (
        <Card title="Tichete">
          <DataTable
            size="small"
            value={ticketsReport}
            lazy
            paginator
            first={first}
            rows={rows}
            rowsPerPageOptions={defaultPaginationOptions}
            totalRecords={pagination.totalCount}
            emptyMessage={<NoResultsFound />}
            rowClassName={(rowData) => (rowData.slaExceeded ? "text-red-400 cursor-pointer" : "cursor-pointer")}
            onRowClick={(e) => navigate(`${routes.agentTicketDetails.navigatePath}/${e.data.id}`)}
            onPage={(e) => onPageChange(e.first, e.rows)}
          >
            <Column header="Id" field="prettyId" />
            <Column header="Tip" field="type" />
            <Column header="Prioritate" field="urgency" />
            <Column header="Status" field="status" />
            <Column header="Categorie" field="categoryName" />
            <Column header="Locatie" field="location" />
            <Column header="Grupa" field="teamName" />
            <Column header="Agent" field="agentName" />
            <Column header="Utilizator" field="userName" />
            <Column className="w-7rem" header="Timp logat" field="totalLoggedTime" />
            <Column className="w-9rem" header="Data rezolvare" field="resolvedDate" />
          </DataTable>
        </Card>
      )}
    </div>
  );
}
